import React from "react";
import { useState, useEffect } from "react";
import { api } from "../../api/client";
import { Loader } from "../Loader";

const TabStops = () => {
  const [stops, setStops] = useState([]);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [stopForm, setStopForm] = useState({ name: "", order: "" });
  const [editingStopId, setEditingStopId] = useState(null);
  const [bulkSelectedStops, setBulkSelectedStops] = useState([]);

  useEffect(() => {
    loadStops();
  }, []);

  const loadStops = async () => {
    setStopsLoading(true);
    try {
      const r = await api("/admin/stops");
      setStops(r.stops);
    } catch (err) {
      alert(err.message || "Failed to load stops");
    }
    setStopsLoading(false);
  };

  const createOrUpdateStop = async (e) => {
    e.preventDefault();
    if (!stopForm.name || stopForm.order === "") {
      alert("Name and order are required");
      return;
    }
    try {
      if (editingStopId) {
        await api(`/admin/stops/${editingStopId}`, {
          method: "PUT",
          body: stopForm,
        });
      } else {
        await api("/admin/stops", {
          method: "POST",
          body: stopForm,
        });
      }
      setStopForm({ name: "", order: "" });
      setEditingStopId(null);
      loadStops();
    } catch (err) {
      alert(err.message || "Failed to save stop");
    }
  };

  const editStop = (stop) => {
    setStopForm({ name: stop.name, order: stop.order });
    setEditingStopId(stop._id);
    document.querySelector('form.input-row').scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const cancelEditStop = () => {
    setStopForm({ name: "", order: "" });
    setEditingStopId(null);
  };

  const deleteStop = async (id) => {
    if (
      window.confirm(
        "Delete this stop? This will shift all subsequent stops down by one order number."
      )
    ) {
      try {
        await api(`/admin/stops/${id}`, { method: "DELETE" });
        loadStops();
      } catch (err) {
        alert(err.message || "Failed to delete stop");
      }
    }
  };

  const bulkDeleteStops = async () => {
    if (bulkSelectedStops.length === 0) {
      alert("No stops selected");
      return;
    }
    if (window.confirm(`Delete ${bulkSelectedStops.length} stop(s)?`)) {
      try {
        await api("/admin/stops/bulk-delete", {
          method: "POST",
          body: { stopIds: bulkSelectedStops },
        });
        setBulkSelectedStops([]);
        loadStops();
      } catch (err) {
        alert(err.message || "Failed to delete stops");
      }
    }
  };

  const toggleStopSelection = (stopId) => {
    setBulkSelectedStops((prev) =>
      prev.includes(stopId)
        ? prev.filter((id) => id !== stopId)
        : [...prev, stopId]
    );
  };

  const toggleSelectAll = () => {
    const allStopIds = stops.map((s) => s._id);
    const allSelected = allStopIds.every((id) => bulkSelectedStops.includes(id));

    if (allSelected) {
      setBulkSelectedStops([]);
    } else {
      setBulkSelectedStops(allStopIds);
    }
  };

  return (
    <div className="card">
      <h3>Manage Stops</h3>
      <p>
        <i>
          Stops are ordered by their order number. When you add a new stop with
          an existing order number, all subsequent stops will be shifted down
          automatically.
        </i>
      </p>
      {stopsLoading ? (
        <Loader />
      ) : (
        <>
          <h3>{editingStopId ? "Edit Stop" : "Add New Stop"}</h3>
          <form
            onSubmit={createOrUpdateStop}
            className="input-row"
            style={{ gap: 12, marginBottom: 10 }}
          >
            <input
              type="number"
              placeholder="Order Number"
              value={stopForm.order}
              onChange={(e) =>
                setStopForm((f) => ({ ...f, order: e.target.value }))
              }
              required
              min="0"
            />
            <input
              placeholder="Stop Name"
              value={stopForm.name}
              onChange={(e) =>
                setStopForm((f) => ({ ...f, name: e.target.value }))
              }
              required
            />
            <button className="btn success" type="submit">
              {editingStopId ? "Update Stop" : "Add Stop"}
            </button>
            {editingStopId && (
              <button
                type="button"
                className="btn"
                onClick={cancelEditStop}
                style={{ background: "#888" }}
              >
                Cancel
              </button>
            )}
          </form>
          <div className="divider" />

          {bulkSelectedStops.length > 0 && (
            <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center" }}>
              <span style={{ fontWeight: 600, color: "#2d7a64" }}>
                {bulkSelectedStops.length} selected
              </span>
              <button
                className="btn danger"
                onClick={bulkDeleteStops}
              >
                Delete Selected
              </button>
              <button
                className="btn secondary"
                onClick={() => setBulkSelectedStops([])}
              >
                Clear Selection
              </button>
            </div>
          )}

          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>
                    <input
                      type="checkbox"
                      checked={
                        stops.length > 0 &&
                        stops.every((s) => bulkSelectedStops.includes(s._id))
                      }
                      onChange={toggleSelectAll}
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                  <th>Order</th>
                  <th>Stop Name</th>
                  {bulkSelectedStops.length === 0 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {stops.length === 0 && (
                  <tr>
                    <td colSpan={4}>No stops yet</td>
                  </tr>
                )}
                {stops.map((stop) => (
                  <tr key={stop._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={bulkSelectedStops.includes(stop._id)}
                        onChange={() => toggleStopSelection(stop._id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td>{stop.order}</td>
                    <td>{stop.name}</td>
                    {bulkSelectedStops.length === 0 && (
                      <td>
                        <button
                          className="btn success"
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.4rem 0.7rem",
                            marginRight: "0.5rem",
                          }}
                          onClick={() => editStop(stop)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn danger"
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.4rem 0.7rem",
                          }}
                          onClick={() => deleteStop(stop._id)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TabStops;
